import { LoadingSkeletonDirective2 } from 'common-ui';
import { ErrorComponent } from 'error-ui';
import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, signal, untracked } from '@angular/core';
import { ElementResizeDirective } from 'common-util';
import { CartComponentStore, CartItemComponentStore } from 'cart-data';
import { ItemPricingModel } from 'products-data';
import { ItemPriceViewComponent, ProductNotReturnableViewComponent } from 'products-ui';
import { CartItemQuantitySelectorViewComponent } from '../../../common';
import { RenderInCartItemResultLayoutDirective } from '../../cart-item/cart-item-result-layout/render-targets';
import { SavedPartListItemResultLayoutComponentsProviderComponentBase } from '../SavedPartListItemResultLayoutComponentsProviderComponentBase';

/** Below this quantity column width (in em) the field label drops to its short form, as in ideal-cart-item-quantity-selector. */
const COMPACT_LABEL_EM = 14;

@Component({
  selector: 'ideal-saved-part-list-item-component-provider',
  imports: [
    RenderInCartItemResultLayoutDirective,
    CartItemQuantitySelectorViewComponent,
    ItemPriceViewComponent,
    ProductNotReturnableViewComponent,
    LoadingSkeletonDirective2,
    ErrorComponent,
    ElementResizeDirective,
  ],
  templateUrl: './saved-part-list-item-component-provider.component.html',
  styleUrl: './saved-part-list-item-component-provider.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CartComponentStore],
})
export class SavedPartListItemComponentProviderComponent extends SavedPartListItemResultLayoutComponentsProviderComponentBase {
  private readonly _store = inject(CartItemComponentStore);
  private readonly _cartStore = inject(CartComponentStore);

  /** The cart line's pricing state; the price views here don't go through ideal-item-price, which normally shows it. */
  protected readonly pricingLoading = this._store.cartItemPricingLoading;
  protected readonly pricingError = computed(() => (this._store.cartItemPricingLoading() ? undefined : this._store.cartItemPricingError()));
  protected readonly itemQuantity = this._store.cartItemQuantity;

  protected readonly cartReady = computed(
    () => !this._cartStore.cartLoading() && !this._cartStore.cartError() && this._cartStore.cart() != null,
  );
  protected readonly cartItem = computed(() => this._cartStore.cartItems().find((item) => item.id.equals(this.itemId())));
  protected readonly cartItemPrice = computed(
    () => this._cartStore.cartPrices().find((item) => item.itemId.equals(this.itemId()))?.pricing,
  );
  protected readonly cartItemPricingModel = computed(() => {
    const cartItemPrice = this._store.cartItemPricing();
    const pricingModel = cartItemPrice?.pricingModel;
    if (!cartItemPrice || !pricingModel) {
      return undefined;
    }
    const result = {
      ...pricingModel,
    } as ItemPricingModel;
    return result;
  });

  protected readonly isItemPending = computed(() => {
    const itemId = this.itemId();
    if (!itemId) {
      return false;
    }
    this._cartStore.pendingCartItems();
    return this._cartStore.isItemPending(itemId);
  });

  /** Blank, not 0, when the item isn't in the cart. */
  protected readonly cartItemQuantity = signal<number | undefined>(undefined);
  protected readonly initialized = signal(false);

  private readonly _elm: HTMLElement = inject(ElementRef).nativeElement;
  private _fontSizePx: number | undefined;
  /** Quantity column too narrow for the full label. */
  protected readonly compactQuantity = signal(false);

  constructor() {
    super();
    effect(() => {
      if (this.cartReady()) {
        const cartItem = this.cartItem();
        if (cartItem) {
          const quantity = cartItem.quantity;
          this.cartItemQuantity.set(quantity);
        } else if (untracked(this.cartItemQuantity) !== undefined) {
          this.cartItemQuantity.set(undefined);
        }
        if (!this.initialized()) {
          this.initialized.set(true);
        }
      }
    });
    effect(() => {
      if (this.initialized()) {
        const quantity = this.cartItemQuantity() ?? 0;
        const oldQuantity = untracked(this.cartItem)?.quantity ?? 0;
        if (quantity !== oldQuantity) {
          untracked(() => this._cartStore.updateItemQuantity(this.itemId()!, quantity));
        }
      }
    });
  }

  protected onQuantityWidthChange(width: number) {
    this._fontSizePx ??= parseFloat(getComputedStyle(this._elm).fontSize) || 16;
    this.compactQuantity.set(width < COMPACT_LABEL_EM * this._fontSizePx);
  }
}
